/**
 * LocationFilter — selector múltiple de ubicaciones con jerarquía.
 *
 * - Selección múltiple: marca tantas ubicaciones como quieras.
 * - Jerarquía: si una ubicación tiene hijas, al marcarla se incluyen sus
 *   descendientes (`includeChildren`, activado por defecto). Se muestra como
 *   árbol para que se vea qué es grupo y qué es subgrupo.
 * - Sin selección = «Todas».
 *
 * Props:
 *   companyId   – empresa activa
 *   value       – array de ids seleccionados
 *   onChange    – (ids: number[]) => void
 *   label       – texto del botón (por defecto «Ubicaciones»)
 *   extraItems  – [{ id, name, parent_id }] ubicaciones ya conocidas (opcional)
 *   showPath    – muestra la ruta completa de cada ubicación (organigrama)
 *   counts      – { [locationId]: número } para ver cuántos elementos cuelgan
 *   onlyIds     – limita la lista a esas ubicaciones (más sus ancestros, para
 *                 que la ruta siga teniendo sentido)
 *   panelAlign  – 'right' (por defecto) | 'left': lado al que se abre el panel.
 *                 En una barra de herramientas alineada a la izquierda hay que
 *                 pasar 'left' o el panel se sale de la pantalla.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { buildLocationMap, locationPath } from '../utils/locationTree';

/** Raíces primero y luego cada rama; devuelve [{ location, depth }]. */
export function flattenLocations(locations) {
  const childrenOf = new Map();
  const roots = [];
  const byId = new Map();
  locations.forEach((loc) => { byId.set(loc.id, loc); });
  locations.forEach((loc) => {
    const parent = loc.parent_id && byId.has(loc.parent_id) ? loc.parent_id : null;
    if (parent === null) roots.push(loc);
    else {
      if (!childrenOf.has(parent)) childrenOf.set(parent, []);
      childrenOf.get(parent).push(loc);
    }
  });
  const out = [];
  const walk = (nodes, depth) => {
    nodes
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .forEach((node) => {
        out.push({ location: node, depth });
        walk(childrenOf.get(node.id) || [], depth + 1);
      });
  };
  walk(roots, 0);
  // Huérfanas (parent_id apunta fuera): no se pierden.
  const seen = new Set(out.map((entry) => entry.location.id));
  locations
    .filter((loc) => !seen.has(loc.id))
    .forEach((loc) => out.push({ location: loc, depth: 0 }));
  return out;
}

/** Ids de una ubicación más todas sus descendientes. */
export function withDescendants(locations, ids) {
  const childrenOf = new Map();
  locations.forEach((loc) => {
    if (!loc.parent_id) return;
    if (!childrenOf.has(loc.parent_id)) childrenOf.set(loc.parent_id, []);
    childrenOf.get(loc.parent_id).push(loc.id);
  });
  const out = new Set();
  const walk = (id) => {
    if (out.has(id)) return;
    out.add(id);
    (childrenOf.get(id) || []).forEach(walk);
  };
  (ids || []).forEach(walk);
  return out;
}

/**
 * ¿El elemento de esta ubicación entra en el filtro?
 * Sin selección entra todo. Con selección entra si su ubicación (o un padre
 * suyo) está marcada.
 */
export function matchesLocation(locations, selectedIds, locationId) {
  if (!selectedIds || selectedIds.length === 0) return true;
  if (locationId === null || locationId === undefined) return false;
  const byId = new Map(locations.map((loc) => [loc.id, loc]));
  let current = byId.get(locationId);
  const seen = new Set();
  while (current && !seen.has(current.id)) {
    if (selectedIds.includes(current.id)) return true;
    seen.add(current.id);
    current = current.parent_id ? byId.get(current.parent_id) : null;
  }
  return false;
}

export default function LocationFilter({
  companyId,
  value = [],
  onChange,
  label = 'Ubicaciones',
  extraItems = [],
  showPath = false,
  counts = null,
  onlyIds = null,
  panelAlign = 'right',
}) {
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null);
  const panelRef = useRef(null);

  // En pantalla completa el navegador SOLO pinta el subárbol del elemento que
  // está en fullscreen; un portal al `body` se queda fuera y el desplegable no
  // aparecía (parecía que el botón «no hacía nada»).
  const portalHost = (typeof document !== 'undefined' && document.fullscreenElement)
    || (typeof document !== 'undefined' ? document.body : null);

  useEffect(() => {
    let alive = true;
    if (!companyId) return undefined;
    api
      .getLocations('', companyId)
      .then((res) => {
        if (!alive) return;
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setLocations(rows);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId]);

  // Las ubicaciones que llegan por props (p.ej. del lienzo) cubren huecos si
  // la API devuelve menos de las que aparecen en los equipos.
  const allLocations = useMemo(() => {
    const map = new Map();
    locations.forEach((loc) => map.set(loc.id, loc));
    extraItems.forEach((loc) => {
      if (loc?.id && !map.has(loc.id)) map.set(loc.id, loc);
    });
    return [...map.values()];
  }, [locations, extraItems]);

  const tree = useMemo(() => flattenLocations(allLocations), [allLocations]);
  const pathMap = useMemo(() => buildLocationMap(allLocations), [allLocations]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  // Si llega `onlyIds`, la lista se queda con esas ubicaciones y con todos sus
  // ancestros: sin la rama completa, «Sala 1» no se puede situar en su sede.
  const scopeIds = useMemo(() => {
    if (!onlyIds) return null;
    const byId = new Map(allLocations.map((loc) => [String(loc.id), loc]));
    const keep = new Set();
    [...onlyIds].forEach((raw) => {
      let current = byId.get(String(raw));
      const seen = new Set();
      while (current) {
        const key = String(current.id);
        if (seen.has(key)) break;
        seen.add(key);
        keep.add(key);
        current = current.parent_id != null ? byId.get(String(current.parent_id)) : null;
      }
    });
    return keep;
  }, [allLocations, onlyIds]);

  const filteredTree = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tree.filter(({ location }) => {
      if (scopeIds && !scopeIds.has(String(location.id))) return false;
      if (!term) return true;
      const ruta = locationPath(pathMap, location.id) || '';
      return `${location.name || ''} ${location.code || ''} ${location.building_name || ''} ${ruta}`
        .toLowerCase()
        .includes(term);
    });
  }, [tree, search, scopeIds, pathMap]);

  const toggle = useCallback((location) => {
    const expanded = withDescendants(allLocations, [location.id]);
    const isOn = selectedSet.has(location.id);
    const next = new Set(value);
    if (isOn) {
      // Se quita la ubicación y sus descendientes.
      expanded.forEach((id) => next.delete(id));
    } else {
      expanded.forEach((id) => next.add(id));
    }
    onChange([...next]);
  }, [allLocations, onChange, selectedSet, value]);

  // El desplegable se pinta en `position: fixed` medido desde el botón y en un
  // portal al `body`: dentro de la topología WiFi (`.wtopo-page` crea contexto
  // de apilado con z-index 1) un `absolute` quedaba DEBAJO del menú lateral
  // (z-index 1100) y recortado por el `overflow: hidden` de la página.
  const medir = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = showPath ? 360 : 300;
    const deseada = panelAlign === 'left' ? r.left : r.right - width;
    const left = Math.max(8, Math.min(deseada, vw - width - 8));
    const alto = Math.min(380, vh - 24);
    const abajo = vh - r.bottom - 12;
    const arriba = r.top - 12;
    const haciaArriba = abajo < Math.min(220, alto) && arriba > abajo;
    setPos({
      left: Math.round(left),
      width,
      top: haciaArriba ? undefined : Math.round(r.bottom + 6),
      bottom: haciaArriba ? Math.round(vh - r.top + 6) : undefined,
      maxHeight: Math.round(Math.max(160, Math.min(alto, haciaArriba ? arriba : abajo))),
    });
  }, [panelAlign, showPath]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (boxRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onScroll = () => medir();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    // Al cambiar de modo el elemento de referencia se mueve: se vuelve a medir
    // (y el render recoloca el portal dentro del nodo en pantalla completa).
    document.addEventListener('fullscreenchange', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('fullscreenchange', onScroll);
    };
  }, [open, medir]);

  // Resumen del botón: se muestran las ubicaciones marcadas «de cabeza». Al
  // marcar una madre se marcan también sus hijas, pero resumir «3 ubicaciones»
  // esconde justo lo que el usuario ha elegido («Barcelona»).
  const summary = useMemo(() => {
    if (!value.length) return 'Todas';
    const selected = new Set(value);
    const byId = new Map(allLocations.map((loc) => [loc.id, loc]));
    const top = allLocations.filter((loc) => {
      if (!selected.has(loc.id)) return false;
      const seen = new Set([loc.id]);
      let parent = loc.parent_id != null ? byId.get(loc.parent_id) : null;
      while (parent) {
        if (selected.has(parent.id)) return false;
        if (seen.has(parent.id)) break;
        seen.add(parent.id);
        parent = parent.parent_id != null ? byId.get(parent.parent_id) : null;
      }
      return true;
    });
    const names = top.map((loc) => loc.name).filter(Boolean);
    if (!names.length) return `${value.length} ubicaciones`;
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1}`;
  }, [value, allLocations]);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="list-btn-secondary"
        onClick={() => setOpen((prev) => { if (!prev) medir(); return !prev; })}
        title="Filtrar por ubicación (varias a la vez)"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <span aria-hidden="true">📍</span>
        {label}: <strong>{summary}</strong>
        {value.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            title="Quitar el filtro"
            style={{ marginLeft: 4, opacity: 0.7 }}
            onClick={(event) => { event.stopPropagation(); onChange([]); }}
            onKeyDown={(event) => { if (event.key === 'Enter') onChange([]); }}
          >
            ✕
          </span>
        )}
      </button>

      {open && pos && portalHost && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            bottom: pos.bottom,
            width: pos.width,
            maxHeight: pos.maxHeight,
            zIndex: 3000,
            overflow: 'auto',
            /* OJO: --bg-card es TRANSLÚCIDO en los skins Lumina (se veía el
               lienzo a través y la lista parecía quedar detrás). La superficie
               opaca del design system es --bg-surface. */
            background: 'var(--bg-surface, #0d0f1a)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
            padding: 10,
          }}
        >
          <input
            className="list-input"
            style={{ width: '100%', marginBottom: 8 }}
            placeholder="Buscar ubicación…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <button type="button" className="list-btn-secondary" onClick={() => onChange([])}>
              Todas
            </button>
            <span className="list-hint">{value.length} marcada(s)</span>
          </div>
          {filteredTree.length === 0 && (
            <div className="list-hint" style={{ padding: '6px 2px' }}>
              No hay ubicaciones.
            </div>
          )}
          {filteredTree.map(({ location, depth }) => {
            const checked = selectedSet.has(location.id);
            const hasChildren = allLocations.some((loc) => loc.parent_id === location.id);
            // Ruta completa: dos «Sala 1» de plantas distintas se distinguen por
            // su rama, no por el nombre suelto.
            const path = showPath ? (locationPath(pathMap, location.id) || location.name) : null;
            const count = counts ? counts[location.id] : null;
            return (
              <label
                key={location.id}
                className="list-hint"
                title={path || location.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 2px',
                  paddingLeft: 2 + depth * 14,
                  cursor: 'pointer',
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(location)} />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: hasChildren ? 600 : 400, color: 'var(--text-primary)' }}>
                    {location.name || `Ubicación ${location.id}`}
                  </span>
                  {path && (
                    <span
                      style={{
                        fontSize: 10.5, lineHeight: 1.2, color: 'var(--text-muted)',
                        maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {path}
                    </span>
                  )}
                </span>
                {count != null && count > 0 && (
                  <span className="list-badge list-badge--muted" title="Elementos que cuelgan de esta rama">
                    {count}
                  </span>
                )}
                {hasChildren && (
                  <span className="list-badge list-badge--muted" title="Tiene subgrupos">
                    grupo
                  </span>
                )}
                {location.floor && <span className="list-hint">{location.floor}</span>}
              </label>
            );
          })}
          <div className="list-hint" style={{ marginTop: 8 }}>
            Al marcar un grupo se incluyen sus subgrupos.
          </div>
        </div>,
        portalHost,
      )}
    </div>
  );
}
